import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

import { ConfigProvider, Pagination, Spin } from 'antd'
import * as React from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

interface PdfViewerProps {
  url: string
  width?: number | string
  height?: number | string
  showPagination?: boolean
  className?: string
  onLoadSuccess?: (numPages: number) => void
  onLoadError?: (error: Error) => void
}

const ViewerContainer = styled.div<{
  width?: number | string
  height?: number | string
}>`
  width: ${({ width }) =>
    typeof width === 'number' ? `${width}px` : width || '100%'};
  height: ${({ height }) =>
    typeof height === 'number' ? `${height}px` : height || '400px'};
  overflow: auto;
  border-radius: 8px;
  background: ${CantileverTheme.colors.grayscale[100]};
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  color: ${CantileverTheme.colors.error[500]};
  text-align: center;
  padding: 16px;
`

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
  background: ${CantileverTheme.colors.grayscale[100]};
  border-top: 1px solid ${CantileverTheme.colors.grayscale[300]};
`

const DocumentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const PageContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 16px;
  overflow: auto;
`

const calculatePageWidth = () => {
  const containerWidth = window.innerWidth * 0.8
  const minWidth = 600 // Increased from 400
  const maxWidth = 1200 // Added max width to prevent excessive scaling
  return Math.min(Math.max(containerWidth, minWidth), maxWidth)
}

export const PdfViewer: React.FC<PdfViewerProps> = ({
  url,
  width,
  height = 1000,
  showPagination = true,
  className,
  onLoadSuccess,
  onLoadError,
}) => {
  const [numPages, setNumPages] = React.useState<number | null>(null)
  const [currentPage, setCurrentPage] = React.useState<number>(1)
  const [loading, setLoading] = React.useState<boolean>(true)
  const [error, setError] = React.useState<string | null>(null)

  const onDocumentLoadSuccess = React.useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages)
      setLoading(false)
      setError(null)
      onLoadSuccess?.(numPages)
    },
    [onLoadSuccess]
  )

  const onDocumentLoadError = React.useCallback(
    (error: Error) => {
      setError(error.message)
      setLoading(false)
      onLoadError?.(error)
    },
    [onLoadError]
  )

  const handlePageChange = React.useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  // Reset current page when URL changes
  React.useEffect(() => {
    setCurrentPage(1)
    setLoading(true)
    setError(null)
  }, [url])

  return (
    <ViewerContainer width={width} height={height} className={className}>
      <DocumentContainer>
        {loading && (
          <LoadingContainer>
            <Spin size='large' />
          </LoadingContainer>
        )}
        {error && <ErrorContainer>Error loading PDF: {error}</ErrorContainer>}
        <Document
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <LoadingContainer>
              <Spin size='large' />
            </LoadingContainer>
          }>
          <PageContainer>
            <Page
              pageNumber={currentPage}
              renderMode='canvas'
              width={calculatePageWidth()}
              scale={1.0}
            />
          </PageContainer>
        </Document>
        {showPagination && numPages && numPages > 1 && (
          <PaginationContainer>
            <ConfigProvider
              theme={{
                token: {
                  colorText: '#000',
                },
                components: {
                  Pagination: {
                    itemActiveBg: CantileverTheme.colors.grayscale[300],
                  },
                },
              }}>
              <Pagination
                current={currentPage}
                total={numPages}
                pageSize={1}
                showSizeChanger={false}
                showQuickJumper={false}
                showTotal={(total) => `Page ${currentPage} of ${total}`}
                onChange={handlePageChange}
                size='small'
              />
            </ConfigProvider>
          </PaginationContainer>
        )}
      </DocumentContainer>
    </ViewerContainer>
  )
}
